import * as React from "react";
import { useFloating, offset, flip, shift, autoUpdate, useListNavigation, useInteractions } from "@floating-ui/react";
const DropdownContext = React.createContext(null);
DropdownContext.displayName = "DropdownContext";
const useDropdownContext = () => {
  const context = React.useContext(DropdownContext);
  if (context === null) {
    throw new Error("No DropdownContext available.");
  }
  return context;
};
const ButtonDropdownProvider = ({
  "aria-label": ariaLabel,
  children,
  isDisabled,
  isInvalid,
  isOpen,
  offset: offset$1 = 3,
  //offset in pixels for the offset of the floating content box
  placement = "bottom-start",
  isRequired,
  setIsOpen,
  onChange,
  value: valueProp,
  cols = 1,
  itemSizes,
  focusItemOnHover = true,
  orientation = "vertical",
  loop
}) => {
  const [value, setValue] = React.useState(valueProp);
  const contentId = React.useId();
  const { context: floatingContext } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: (floatOpen) => {
      floatOpen && setIsOpen(true);
    },
    whileElementsMounted: autoUpdate,
    middleware: [offset(offset$1), flip(), shift()]
  });
  const itemsRef = React.useRef([]);
  const [activeIndex, setActiveIndex] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const listNavigation = useListNavigation(floatingContext, {
    listRef: itemsRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    focusItemOnHover,
    orientation,
    cols,
    itemSizes,
    loop
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([listNavigation]);
  const open = React.useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);
  const close = React.useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);
  const toggle = React.useCallback(() => {
    setIsOpen((prevValue) => !prevValue);
  }, [setIsOpen]);
  const handleSelect = React.useCallback(
    (index, value2) => {
      setSelectedIndex(index);
      setIsOpen(false);
      setValue(value2);
      onChange?.(value2);
    },
    [onChange, setIsOpen]
  );
  React.useEffect(() => {
    if (valueProp !== value) {
      setValue(valueProp);
    }
  }, [valueProp, value]);
  return /* @__PURE__ */ React.createElement(
    DropdownContext.Provider,
    {
      value: {
        "aria-label": ariaLabel,
        contentId,
        isDisabled,
        isInvalid,
        isOpen,
        floatingProps: {
          floatingContext,
          getReferenceProps,
          getFloatingProps,
          getItemProps,
          itemsRef
        },
        isRequired,
        value,
        toggle,
        open,
        close,
        cols,
        activeIndex,
        selectedIndex,
        handleSelect
      }
    },
    children
  );
};
export {
  ButtonDropdownProvider,
  useDropdownContext
};
//# sourceMappingURL=ButtonDropdownContext.js.map
